import React from 'react'
import config from '../../../constants/config'
import { getBase64, useTranslation, formatDateTime } from '../../../constants/helpers'

const Mau3 = ({ id, player, season, team, sponsors, isShowBirthDay, onSuccess }) => {
    const { t } = useTranslation()
    const width = 640
    const height = width * 1.41
    const px = width / 100

    if (!player) return null

    const isPlayer = player?.types?.[0] === 'football_player_type'
    const positionLabel = isPlayer
        ? `${t(player?.types?.[0])}`
        : `${t(player?.positions?.[0])}`

    return (
        <div
            id={id}
            style={{
                width,
                height,
                position: 'relative',
                overflow: 'hidden',
                background: 'linear-gradient(145deg, #0a0f2e 0%, #0d1a3a 40%, #0a0e28 100%)',
                fontFamily: 'SFUHelveticaCondensedBold'
            }}
        >
            {/* ---- Liquid blob background shapes ---- */}
            <div style={{ position: 'absolute', top: `-${px * 5}px`, left: `-${px * 10}px`, width: `${px * 55}px`, height: `${px * 55}px`, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,120,255,0.28) 0%, transparent 70%)', filter: 'blur(18px)' }} />
            <div style={{ position: 'absolute', top: `${px * 10}px`, right: `-${px * 8}px`, width: `${px * 50}px`, height: `${px * 50}px`, borderRadius: '50%', background: 'radial-gradient(circle, rgba(120,0,255,0.22) 0%, transparent 70%)', filter: 'blur(18px)' }} />
            <div style={{ position: 'absolute', top: `${px * 30}px`, left: `${px * 15}px`, width: `${px * 35}px`, height: `${px * 35}px`, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,200,255,0.18) 0%, transparent 70%)', filter: 'blur(12px)' }} />
            <div style={{ position: 'absolute', bottom: `${px * 20}px`, right: `${px * 5}px`, width: `${px * 40}px`, height: `${px * 40}px`, borderRadius: '50%', background: 'radial-gradient(circle, rgba(80,0,220,0.2) 0%, transparent 70%)', filter: 'blur(14px)' }} />
            <div style={{ position: 'absolute', bottom: `${px * 5}px`, left: `-${px * 5}px`, width: `${px * 35}px`, height: `${px * 35}px`, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,160,255,0.16) 0%, transparent 70%)', filter: 'blur(12px)' }} />

            {/* ---- Glass card border ---- */}
            <div style={{ position: 'absolute', inset: 0, borderRadius: `${px * 2}px`, border: '1px solid rgba(120,180,255,0.2)', pointerEvents: 'none' }} />

            {/* ---- TOP HEADER ---- */}
            <div style={{
                position: 'absolute', top: 0, left: 0, right: 0,
                height: `${px * 18}px`,
                background: 'linear-gradient(180deg, rgba(10,20,60,0.85) 0%, rgba(10,20,60,0.3) 100%)',
                backdropFilter: 'blur(6px)',
                borderBottom: '1px solid rgba(120,180,255,0.15)',
                display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                padding: `0 ${px * 4}px`
            }}>
                {/* Team logo */}
                <div style={{
                    width: `${px * 10}px`, height: `${px * 10}px`,
                    borderRadius: '50%',
                    border: '2px solid rgba(120,180,255,0.4)',
                    boxShadow: '0 0 12px rgba(0,120,255,0.3)',
                    background: 'rgba(255,255,255,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'
                }}>
                    <ImageStatic src={team?.logo} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>

                {/* Season name */}
                <div style={{ textAlign: 'center', flex: 1, padding: `0 ${px * 2}px` }}>
                    <p style={{ color: 'rgba(200,220,255,0.9)', fontSize: `${px * 2.8}px`, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', margin: 0, textShadow: '0 0 8px rgba(0,150,255,0.4)' }}>{season?.name}</p>
                </div>

                {/* League logo */}
                <div style={{
                    width: `${px * 10}px`, height: `${px * 10}px`,
                    borderRadius: '50%',
                    border: '2px solid rgba(120,180,255,0.4)',
                    boxShadow: '0 0 12px rgba(0,120,255,0.3)',
                    background: 'rgba(255,255,255,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'
                }}>
                    <ImageStatic src={season?.logo} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
            </div>

            {/* ---- AVATAR ---- */}
            <div style={{
                position: 'absolute',
                top: `${px * 22}px`,
                left: '50%',
                transform: 'translateX(-50%)',
                width: `${px * 42}px`, height: `${px * 42}px`
            }}>
                {/* Outer glow ring */}
                <div style={{
                    position: 'absolute', inset: `-${px * 1.5}px`,
                    borderRadius: '50%',
                    background: 'conic-gradient(from 0deg, rgba(0,120,255,0.7), rgba(120,0,255,0.7), rgba(0,200,255,0.7), rgba(0,120,255,0.7))',
                    filter: `blur(${px * 0.8}px)`
                }} />
                {/* Glass ring */}
                <div style={{
                    position: 'absolute', inset: 0,
                    borderRadius: '50%',
                    border: `${px * 0.5}px solid rgba(180,220,255,0.5)`,
                    boxShadow: `0 0 20px rgba(0,150,255,0.5), inset 0 0 20px rgba(120,180,255,0.1)`,
                    background: 'rgba(255,255,255,0.05)',
                    zIndex: 1
                }} />
                {/* Glass highlight arc */}
                <div style={{
                    position: 'absolute', top: `${px * 1}px`, left: `${px * 3}px`, right: `${px * 3}px`,
                    height: `${px * 8}px`,
                    borderRadius: `${px * 30}px`,
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, transparent 100%)',
                    zIndex: 3
                }} />
                {/* Avatar image */}
                <Image
                    onChange={onSuccess}
                    src={player?.avatar}
                    style={{
                        position: 'absolute', inset: `${px * 1}px`,
                        borderRadius: '50%', objectFit: 'cover',
                        objectPosition: 'center top',
                        backgroundColor: 'rgba(10,20,60,0.8)',
                        zIndex: 2
                    }}
                />
            </div>

            {/* ---- PLAYER INFO ---- */}
            <div style={{
                position: 'absolute',
                top: `${px * 70}px`,
                left: 0, right: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center'
            }}>
                {/* Name */}
                <h1 style={{
                    color: '#fff',
                    fontSize: `${px * 7.5}px`,
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    textAlign: 'center',
                    margin: 0,
                    letterSpacing: `${px * 0.1}px`,
                    textShadow: `0 0 20px rgba(0,150,255,0.5), 0 2px 4px rgba(0,0,0,0.8)`,
                    padding: `0 ${px * 3}px`,
                    lineHeight: 1.1
                }}>{player?.name}</h1>

                {/* Position + shirt number */}
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: `${px * 1.5}px`, gap: `${px * 2}px` }}>
                    <span style={{
                        color: 'rgba(100,200,255,0.9)',
                        fontSize: `${px * 3.2}px`,
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        textShadow: '0 0 10px rgba(0,180,255,0.6)'
                    }}>{positionLabel}</span>

                    {isPlayer && player?.shirtNumber != null && (
                        <>
                            <span style={{ color: 'rgba(180,200,255,0.4)', fontSize: `${px * 2.5}px` }}>/</span>
                            <span style={{
                                background: 'linear-gradient(135deg, rgba(0,100,255,0.35), rgba(100,0,255,0.35))',
                                border: '1px solid rgba(120,180,255,0.4)',
                                borderRadius: `${px * 2}px`,
                                padding: `${px * 0.3}px ${px * 1.5}px`,
                                color: '#fff',
                                fontSize: `${px * 3}px`,
                                fontWeight: 'bold',
                                boxShadow: '0 0 8px rgba(0,120,255,0.3)'
                            }}>#{player?.shirtNumber}</span>
                        </>
                    )}
                </div>
            </div>

            {/* ---- INFO PANEL (DOB + Team) ---- */}
            <div style={{
                position: 'absolute',
                top: `${px * 85}px`,
                left: `${px * 5}px`, right: `${px * 5}px`,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(120,180,255,0.06))',
                border: '1px solid rgba(120,180,255,0.2)',
                borderRadius: `${px * 1.5}px`,
                backdropFilter: 'blur(8px)',
                padding: `${px * 2}px ${px * 3}px`,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: `${px * 0.5}px`
            }}>
                {isShowBirthDay && (
                    <p style={{
                        color: 'rgba(180,210,255,0.85)', fontSize: `${px * 2.8}px`,
                        margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px'
                    }}>
                        {t('birthday')}: <strong style={{ color: '#fff' }}>{formatDateTime(player?.birthday, 'date')}</strong>
                    </p>
                )}
                <p style={{
                    color: '#fff', fontSize: `${px * 3.5}px`,
                    fontWeight: 'bold', margin: 0,
                    textTransform: 'uppercase', textShadow: '0 0 8px rgba(0,150,255,0.3)'
                }}>{team?.name}</p>
            </div>

            {/* ---- SPONSOR BAR ---- */}
            {sponsors?.length > 0 && (
                <div style={{
                    position: 'absolute',
                    bottom: `${px * 2}px`,
                    left: `${px * 3}px`, right: `${px * 3}px`,
                    height: `${px * 8}px`,
                    background: 'linear-gradient(135deg, rgba(0,10,40,0.8), rgba(10,20,60,0.8))',
                    border: '1px solid rgba(120,180,255,0.2)',
                    borderRadius: `${px * 1.2}px`,
                    backdropFilter: 'blur(6px)',
                    display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                    gap: `${px}px`, padding: `0 ${px * 2}px`
                }}>
                    {sponsors?.slice(0, 8)?.map((item, idx) => (
                        <ImageStatic key={idx} src={item?.logo} style={{ width: `${px * 8}px`, height: `${px * 6}px`, objectFit: 'contain', backgroundColor: '#fff', borderRadius: `${px * 0.4}px` }} />
                    ))}
                </div>
            )}
        </div>
    )
}

// Image with getBase64 + onChange trigger (same pattern as mau1/mau2)
const Image = props => {
    const [data, setData] = React.useState(null)
    React.useEffect(() => {
        if (data) setData(null)
        if (props?.src?.includes('ui-avatars')) {
            getBase64(config.logo).then(r => setData(r))
        } else {
            getBase64(props.src).then(r => setData(r))
        }
    }, [props.src, props.id])
    React.useEffect(() => {
        if (data) props?.onChange?.()
    }, [data])
    return data ? <img {...props} src={data} alt="" /> : null
}

// Static image (header logos – không trigger export)
const ImageStatic = props => {
    const [data, setData] = React.useState(null)
    React.useEffect(() => {
        if (props?.src?.includes('ui-avatars')) {
            getBase64(config.logo).then(r => setData(r))
        } else {
            getBase64(props.src).then(r => setData(r))
        }
    }, [props.src])
    return data ? <img {...props} src={data} alt="" /> : null
}

function areEqual(p, n) {
    return (
        JSON.stringify(p.player) === JSON.stringify(n.player) &&
        JSON.stringify(p.team) === JSON.stringify(n.team) &&
        JSON.stringify(p.season) === JSON.stringify(n.season) &&
        JSON.stringify(p.sponsors) === JSON.stringify(n.sponsors)
    )
}

export default React.memo(Mau3, areEqual)
